import {
  getPlatformExportDescriptor,
  PlatformPublishFormComponent,
} from '@app/modules/social-export/domain/platform-export-descriptors';
import { SocialPlatform } from '@app/modules/social-export/domain/platforms';
import { useAutoSelectConnectionId } from '@app/modules/social-export/hooks/useAutoSelectConnectionId';
import { useStationPlatformConnections } from '@app/modules/social-export/hooks/useStationPlatformConnections';
import {
  SocialExportGetStationConnectionStateDto,
  useSocialExportGetConnectionSettings,
} from '@mi-front/api-v2';
import { useState } from 'react';

export type SocialExportPublishForm = PlatformPublishFormComponent;

export interface UseSocialExportUploadConnectionFlowResult {
  connections: SocialExportGetStationConnectionStateDto[];
  selectedConnectionId: string | null;
  setSelectedConnectionId: (id: string | null) => void;
  areConnectionsResolved: boolean;
  isConnectionsLoading: boolean;
  settingsQuery: ReturnType<typeof useSocialExportGetConnectionSettings>;
  PublishForm: SocialExportPublishForm;
}

/**
 * Shared connection selection, settings fetch, and PublishForm wiring for
 * fragment and composition social-export upload pages.
 */
export function useSocialExportUploadConnectionFlow(
  stationId: string | undefined,
  platform: SocialPlatform,
): UseSocialExportUploadConnectionFlowResult {
  const [selectedConnectionIdState, setSelectedConnectionIdState] = useState<string | null>(null);

  const { connections, areConnectionsResolved, connectionsQuery } = useStationPlatformConnections(
    stationId,
    platform,
  );

  const selectedConnectionId = useAutoSelectConnectionId(
    connections,
    selectedConnectionIdState,
    setSelectedConnectionIdState,
  );

  const settingsQuery = useSocialExportGetConnectionSettings(selectedConnectionId ?? '', {
    query: { enabled: !!selectedConnectionId },
  });

  const { PublishForm } = getPlatformExportDescriptor(platform);

  return {
    connections,
    selectedConnectionId,
    setSelectedConnectionId: setSelectedConnectionIdState,
    areConnectionsResolved,
    isConnectionsLoading: connectionsQuery.isLoading,
    settingsQuery,
    PublishForm,
  };
}
